import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import {
  getAdminStats,
  getPayoutSummary,
  listAdminBookings,
  type AdminBooking,
  type AdminDashboardStats,
  type PayoutSummary,
} from "@/lib/admin";

const money = (n: number) => `$${Math.round(n).toLocaleString("es-MX")}`;
const compactMoney = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n)}`);

type KpiSpec = { label: string; value: string; icon: string; color: string };

/** The three-up KPI row the sales tab already established; reused by every tab. */
function KpiRow({ items }: { items: KpiSpec[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {items.map((kpi) => (
        <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${kpi.color} mb-3`}>
            <i className={`${kpi.icon} text-lg`} />
          </div>
          <p className="text-2xl font-bold text-charcoal">{kpi.value}</p>
          <p className="text-xs text-gray-500 mt-1">{kpi.label}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Monthly column chart. One measure, one hue — the value is printed under every
 * column, which is what keeps a light fill readable rather than relying on the
 * fill's contrast alone.
 */
function MonthlyBars({
  points, formatValue, emptyLabel,
}: {
  points: { key: string; label: string; value: number }[];
  formatValue: (n: number) => string;
  emptyLabel: string;
}) {
  const max = points.length > 0 ? Math.max(...points.map((p) => p.value)) : 0;
  if (points.length === 0 || max <= 0) {
    return <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{emptyLabel}</div>;
  }
  return (
    <div className="h-48 flex items-end gap-4">
      {points.map((p) => (
        <div key={p.key} className="flex-1 flex flex-col items-center gap-2">
          <div
            className="w-full bg-ocean/20 rounded-t-lg transition-all hover:bg-ocean/40"
            style={{ height: `${(p.value / max) * 100}%` }}
            title={`${p.label}: ${formatValue(p.value)}`}
          />
          <span className="text-xs text-gray-500">{p.label}</span>
          <span className="text-xs font-medium text-charcoal">{formatValue(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Ranked magnitude list. Value is direct-labelled on every row, so the bar is a
 *  visual aid rather than the only way to read the number. */
function RankedBars({
  rows, emptyLabel,
}: {
  rows: { key: string; label: string; value: number; sub: string }[];
  emptyLabel: string;
}) {
  const max = rows.length > 0 ? Math.max(...rows.map((r) => r.value)) : 0;
  if (rows.length === 0 || max <= 0) {
    return <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{emptyLabel}</div>;
  }
  return (
    <ol className="space-y-3.5">
      {rows.map((r, i) => (
        <li key={r.key} className="flex items-center gap-3">
          <span className="w-5 shrink-0 text-xs font-medium text-gray-400 tabular-nums">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <span className="text-sm font-medium text-charcoal truncate">{r.label}</span>
              <span className="text-sm font-semibold text-charcoal tabular-nums shrink-0">{money(r.value)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-ocean rounded-full"
                  style={{ width: `${Math.max((r.value / max) * 100, 2)}%` }}
                  title={`${r.label}: ${money(r.value)}`}
                />
              </div>
              <span className="text-xs text-gray-500 shrink-0 tabular-nums">{r.sub}</span>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function toCsv(rows: string[][]): string {
  return rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

export default function AdminReportesPage() {
  const { t } = useTranslation("admin");
  const [activeReport, setActiveReport] = useState<"sales" | "commissions" | "providers" | "products" | "users">("sales");
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Fetched lazily, only when their tab is first opened — the sales tab is the
  // default and must not pay for data it never renders.
  const [payouts, setPayouts] = useState<PayoutSummary[] | null>(null);
  const [bookings, setBookings] = useState<AdminBooking[] | null>(null);
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAdminStats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const needsPayouts = activeReport === "providers" && payouts === null;
    const needsBookings = activeReport === "products" && bookings === null;
    if (!needsPayouts && !needsBookings) return;

    let cancelled = false;
    setTabLoading(true);
    const request = needsPayouts
      ? getPayoutSummary().then((r) => { if (!cancelled) setPayouts(r); })
      : listAdminBookings().then((r) => { if (!cancelled) setBookings(r); });
    request
      .catch(() => { if (!cancelled) { if (needsPayouts) setPayouts([]); else setBookings([]); } })
      .finally(() => { if (!cancelled) setTabLoading(false); });
    return () => { cancelled = true; };
  }, [activeReport, payouts, bookings]);

  const exportBookingsCsv = async () => {
    setExporting(true);
    try {
      const bookings = await listAdminBookings();
      const header = [
        t("admin.rep.csvReference"), t("admin.rep.csvDate"), t("admin.rep.csvTour"), t("admin.rep.csvProvider"),
        t("admin.rep.csvCustomer"), t("admin.rep.csvEmail"), t("admin.rep.csvAdults"), t("admin.rep.csvChildren"),
        t("admin.rep.csvTotal"), t("admin.rep.csvCommission"), t("admin.rep.csvCurrency"), t("admin.rep.csvStatus"),
      ];
      const rows = bookings.map((b) => [
        b.reference, b.date, b.tourTitle, b.providerName, b.customerName, b.customerEmail,
        String(b.adults), String(b.children), String(b.totalPrice), String(b.commissionAmount), b.currency, String(b.status),
      ]);
      const csv = toCsv([header, ...rows]);
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reservas.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const topProviders = useMemo(() => {
    if (!payouts) return [];
    return payouts
      .map((p) => ({
        key: p.providerId,
        label: p.providerName,
        value: p.pendingNet + p.settledNet,
        sub: `${p.pendingCount + p.settledCount}`,
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [payouts]);

  const topProducts = useMemo(() => {
    if (!bookings) return [];
    const byTour = new Map<string, { title: string; gross: number; count: number }>();
    for (const b of bookings) {
      const row = byTour.get(b.tourId) ?? { title: b.tourTitle, gross: 0, count: 0 };
      row.gross += b.totalPrice;
      row.count += 1;
      byTour.set(b.tourId, row);
    }
    return [...byTour.entries()]
      .map(([id, r]) => ({ key: id, label: r.title, value: r.gross, sub: `${r.count}` }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [bookings]);

  const takeRate = stats && stats.totalGrossLifetime > 0
    ? (stats.totalCommissionLifetime / stats.totalGrossLifetime) * 100
    : 0;

  const maxValue = stats && stats.monthly.length > 0 ? Math.max(...stats.monthly.map((d) => d.gross)) : 0;
  const avgTicket = stats && stats.bookingsLifetime > 0 ? stats.totalGrossLifetime / stats.bookingsLifetime : 0;

  return (
    <AdminLayout title={t("admin.rep.title")}>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex gap-2 overflow-x-auto">
          {([
            { key: "sales" as const, label: t("admin.rep.tabSales") },
            { key: "commissions" as const, label: t("admin.rep.tabCommissions") },
            { key: "providers" as const, label: t("admin.rep.tabProviders") },
            { key: "products" as const, label: t("admin.rep.tabProducts") },
            { key: "users" as const, label: t("admin.rep.tabUsers") },
          ]).map((r) => (
            <button
              key={r.key}
              onClick={() => setActiveReport(r.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeReport === r.key ? "bg-ocean text-white" : "bg-white border border-gray-200 text-charcoal hover:bg-gray-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          onClick={exportBookingsCsv}
          disabled={exporting}
          className="inline-flex items-center gap-2 bg-ocean text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-ocean/90 disabled:opacity-50 whitespace-nowrap"
        >
          <i className="ri-download-line" /> {exporting ? t("admin.rep.exporting") : t("admin.rep.export")}
        </button>
      </div>

      {activeReport === "sales" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: t("admin.rep.totalSales"), value: loading ? "…" : money(stats?.totalGrossLifetime ?? 0), icon: "ri-money-dollar-circle-line", color: "bg-ocean/10 text-ocean" },
              { label: t("admin.rep.totalBookings"), value: loading ? "…" : (stats?.bookingsLifetime ?? 0).toLocaleString("es-MX"), icon: "ri-calendar-check-line", color: "bg-turquoise/10 text-turquoise" },
              { label: t("admin.rep.avgTicket"), value: loading ? "…" : money(avgTicket), icon: "ri-bar-chart-line", color: "bg-coral/10 text-coral" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${kpi.color} mb-3`}>
                  <i className={`${kpi.icon} text-lg`} />
                </div>
                <p className="text-2xl font-bold text-charcoal">{kpi.value}</p>
                <p className="text-xs text-gray-500 mt-1">{kpi.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
            <h2 className="text-base font-bold text-charcoal mb-4">{t("admin.rep.monthlySales")}</h2>
            {loading ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.loading")}</div>
            ) : !stats || stats.monthly.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.rep.noSales")}</div>
            ) : (
              <div className="h-48 flex items-end gap-4">
                {stats.monthly.map((d) => (
                  <div key={d.yearMonth} className="flex-1 flex flex-col items-center gap-2">
                    <div
                      className="w-full bg-ocean/20 rounded-t-lg hover:bg-ocean/40 transition-all"
                      style={{ height: `${maxValue > 0 ? (d.gross / maxValue) * 100 : 0}%` }}
                    />
                    <span className="text-xs text-gray-500">{d.label}</span>
                    <span className="text-xs font-medium text-charcoal">${(d.gross / 1000).toFixed(0)}k</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeReport === "commissions" && (
        <div className="space-y-6">
          <KpiRow items={[
            { label: t("admin.rep.totalCommission"), value: loading ? "…" : money(stats?.totalCommissionLifetime ?? 0), icon: "ri-percent-line", color: "bg-ocean/10 text-ocean" },
            { label: t("admin.rep.commission30d"), value: loading ? "…" : money(stats?.commission30d ?? 0), icon: "ri-calendar-line", color: "bg-turquoise/10 text-turquoise" },
            { label: t("admin.rep.takeRate"), value: loading ? "…" : `${takeRate.toFixed(1)}%`, icon: "ri-pie-chart-line", color: "bg-coral/10 text-coral" },
          ]} />
          <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
            <h2 className="text-base font-bold text-charcoal mb-4">{t("admin.rep.monthlyCommission")}</h2>
            {loading ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.loading")}</div>
            ) : (
              <MonthlyBars
                points={(stats?.monthly ?? []).map((m) => ({ key: m.yearMonth, label: m.label, value: m.commission }))}
                formatValue={compactMoney}
                emptyLabel={t("admin.rep.noSales")}
              />
            )}
          </div>
        </div>
      )}

      {activeReport === "providers" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <h2 className="text-base font-bold text-charcoal mb-1">{t("admin.rep.topProvidersTitle")}</h2>
          <p className="text-xs text-gray-500 mb-5">{t("admin.rep.topProvidersDesc")}</p>
          {tabLoading && payouts === null ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.loading")}</div>
          ) : (
            <RankedBars rows={topProviders} emptyLabel={t("admin.rep.noProviders")} />
          )}
        </div>
      )}

      {activeReport === "products" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <h2 className="text-base font-bold text-charcoal mb-1">{t("admin.rep.topProductsTitle")}</h2>
          <p className="text-xs text-gray-500 mb-5">{t("admin.rep.topProductsDesc")}</p>
          {tabLoading && bookings === null ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.loading")}</div>
          ) : (
            <RankedBars rows={topProducts} emptyLabel={t("admin.rep.noProducts")} />
          )}
        </div>
      )}

      {activeReport === "users" && (
        <div className="space-y-6">
          <KpiRow items={[
            { label: t("admin.rep.totalUsers"), value: loading ? "…" : (stats?.totalUsers ?? 0).toLocaleString("es-MX"), icon: "ri-user-line", color: "bg-ocean/10 text-ocean" },
            { label: t("admin.rep.activeProviders"), value: loading ? "…" : (stats?.activeProviders ?? 0).toLocaleString("es-MX"), icon: "ri-store-2-line", color: "bg-turquoise/10 text-turquoise" },
            { label: t("admin.rep.activeTours"), value: loading ? "…" : (stats?.activeTours ?? 0).toLocaleString("es-MX"), icon: "ri-sailboat-line", color: "bg-coral/10 text-coral" },
          ]} />
          <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
            <h2 className="text-base font-bold text-charcoal mb-4">{t("admin.rep.monthlyBookings")}</h2>
            {loading ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">{t("admin.loading")}</div>
            ) : (
              <MonthlyBars
                points={(stats?.monthly ?? []).map((m) => ({ key: m.yearMonth, label: m.label, value: m.bookings }))}
                formatValue={(n) => String(Math.round(n))}
                emptyLabel={t("admin.rep.noSales")}
              />
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
